/*
 * Dialogo.js — Diálogos de confirmación y avisos (toasts).
 *
 * Ver diseno/dialogos.md y las tarjetas C-009 (diálogo), C-008 (eliminar/desactivar)
 * y C-011 (avisos) de diseno/CAMBIOS.md.
 *
 * Uso:
 *   const ok = await confirmar({ titulo: '¿Eliminar este archivo?', accion: 'Eliminar archivo', peligro: true });
 *   avisar({ texto: 'MACCHINA SA DE CV se desactivó.', deshacer: () => ... });
 *
 * <DialogoHost /> y <AvisosHost /> se montan una sola vez (en App).
 */
import { useEffect, useRef, useState } from 'react';
import { normalizarNombre } from './estados';
import { BotonIcono } from './Tooltip';

// ───────────── Diálogo de confirmación (C-009) ─────────────

let mostrarDialogo = null;

/**
 * Abre un diálogo y devuelve una promesa:
 *   true           → confirmó
 *   false          → canceló (Cancelar, ✕, Esc o clic fuera en nivel 2)
 *   'alternativa'  → eligió la acción alternativa (ej. "Desactivar en su lugar")
 *
 * opciones:
 *   titulo          Pregunta con verbo y objeto: "¿Eliminar a MACCHINA SA DE CV?"
 *   cuerpo          Texto o JSX con lo que va a pasar, en concreto.
 *   accion          Texto del botón principal, con verbo: "Eliminar cliente". Nunca "OK".
 *   accionEnCurso   Texto mientras se ejecuta: "Eliminando…".
 *   nivel           2 = confirmación simple (foco en la acción); 3 = reforzada (foco en Cancelar).
 *   peligro         true → botón rojo. Solo para eliminar.
 *   cancelar        Texto del botón de cancelar si no es "Cancelar" (ej. "Seguir editando", C-026).
 *   confirmarTexto  Texto que hay que escribir para habilitar el botón (sin distinguir mayúsculas,
 *                   acentos ni espacios de más).
 *   alternativa     { texto, descripcion } → bloque con un botón alternativo antes de la acción.
 *   eleccion        { etiqueta, opciones: [{ valor, texto, detalle }] } → hay que elegir una opción
 *                   (radio) para habilitar la acción; la promesa devuelve el `valor` elegido en vez
 *                   de true. Ninguna viene marcada (C-034: el motivo de "Presentada en $0").
 *   motivo          { etiqueta, ejemplo } → campo de texto obligatorio para habilitar la acción; la
 *                   promesa devuelve el texto escrito en vez de true (ej. por qué se regresa un pago).
 *   ejecutar        async (reportar) => {} opcional. Se corre con el botón en "…en curso"; si falla,
 *                   el diálogo sigue abierto y muestra el error.
 *                   - reportar('Enviando… 23 de 74') cambia el texto de progreso (C-010).
 *                   - El segundo argumento trae { sigueAbierto }, para saber si el usuario lo mandó
 *                     a segundo plano y avisar con un toast en lugar de mostrar el resultado.
 *                   - Si devuelve { resultado: JSX }, el diálogo se queda abierto mostrando ese
 *                     resultado con un solo botón "Cerrar" (ej. "71 enviados, 3 fallaron").
 *   segundoPlano    Texto de un botón que, mientras se ejecuta, cierra el diálogo sin detener el
 *                   proceso (ej. "Seguir en segundo plano"). La promesa devuelve 'segundo-plano'.
 */
export const confirmar = (opciones) =>
  new Promise((resolve) => {
    if (!mostrarDialogo) {
      // Sin host montado: no ejecutar nada irreversible por accidente.
      resolve(false);
      return;
    }
    mostrarDialogo({ nivel: 2, ...opciones, resolve });
  });

const compararTexto = (a) => normalizarNombre(a).replace(/\s+/g, ' ').trim();

export function DialogoHost() {
  const [dialogo, setDialogo] = useState(null);
  const [escrito, setEscrito] = useState('');
  const [elegido, setElegido] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [enCurso, setEnCurso] = useState(false);
  const [error, setError] = useState('');
  const [progreso, setProgreso] = useState('');
  const [resultado, setResultado] = useState(null);
  const actual = useRef(null);
  const cajaRef = useRef(null);
  const cancelarRef = useRef(null);
  const accionRef = useRef(null);
  const inputRef = useRef(null);
  const focoPrevio = useRef(null);
  const abierto = useRef(false);

  useEffect(() => {
    mostrarDialogo = (d) => {
      // Si un diálogo abre otro (ej. "Desactivar en su lugar"), el foco regresa al botón original.
      if (!abierto.current) focoPrevio.current = document.activeElement;
      abierto.current = true;
      setEscrito('');
      setElegido(null);
      setMotivo('');
      setError('');
      setProgreso('');
      setResultado(null);
      setEnCurso(false);
      actual.current = d;
      setDialogo(d);
    };
    return () => { mostrarDialogo = null; };
  }, []);

  // Foco inicial: nivel 3 en Cancelar (Enter nunca ejecuta lo irreversible); nivel 2 en la acción.
  useEffect(() => {
    if (!dialogo) return;
    // Con una elección, la acción empieza deshabilitada: el foco va a la primera opción.
    const primeraOpcion = dialogo.eleccion ? cajaRef.current?.querySelector('.dialogo-eleccion input')
      : dialogo.motivo ? cajaRef.current?.querySelector('.dialogo-motivo textarea') : null;
    const destino = primeraOpcion || (dialogo.nivel >= 3 ? cancelarRef.current : accionRef.current);
    destino?.focus();
  }, [dialogo]);

  if (!dialogo) return null;

  const cerrar = (valor, { aunqueEnCurso = false } = {}) => {
    if (enCurso && !aunqueEnCurso) return;
    const { resolve } = dialogo;
    abierto.current = false;
    actual.current = null;
    setEnCurso(false);
    setDialogo(null);
    resolve(valor);
    const anterior = focoPrevio.current;
    setTimeout(() => {
      if (!abierto.current && anterior && document.contains(anterior)) anterior.focus();
    }, 0);
  };

  const textoCoincide = (!dialogo.confirmarTexto || compararTexto(escrito) === compararTexto(dialogo.confirmarTexto))
    && (!dialogo.eleccion || elegido !== null)
    && (!dialogo.motivo || motivo.trim() !== '');

  const confirmarAccion = async () => {
    if (!textoCoincide || enCurso) return;
    if (!dialogo.ejecutar) {
      cerrar(dialogo.eleccion ? elegido : dialogo.motivo ? motivo.trim() : true);
      return;
    }
    const este = dialogo;
    const sigueAbierto = () => actual.current === este;
    setEnCurso(true);
    setError('');
    setProgreso('');
    try {
      const salida = await este.ejecutar(
        (texto) => { if (sigueAbierto()) setProgreso(texto); },
        { sigueAbierto },
      );
      if (!sigueAbierto()) return; // se cerró con "Seguir en segundo plano"
      setEnCurso(false);
      if (salida && salida.resultado) {
        setResultado(salida.resultado);
        return;
      }
      abierto.current = false;
      actual.current = null;
      setDialogo(null);
      este.resolve(true);
    } catch (e) {
      console.error(e);
      if (!sigueAbierto()) return;
      setEnCurso(false);
      setError(e?.message ? `No se pudo completar: ${e.message}` : 'No se pudo completar. Intenta de nuevo.');
    }
  };

  // Esc = Cancelar; Tab se queda dentro del diálogo.
  const alTeclear = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      cerrar(resultado ? true : false);
      return;
    }
    if (e.key !== 'Tab' || !cajaRef.current) return;
    const enfocables = [...cajaRef.current.querySelectorAll('button:not([disabled]), input:not([disabled]), textarea:not([disabled])')];
    if (enfocables.length === 0) return;
    const primero = enfocables[0];
    const ultimo = enfocables[enfocables.length - 1];
    if (e.shiftKey && document.activeElement === primero) {
      e.preventDefault();
      ultimo.focus();
    } else if (!e.shiftKey && document.activeElement === ultimo) {
      e.preventDefault();
      primero.focus();
    }
  };

  const nivel3 = dialogo.nivel >= 3;

  return (
    <div
      className="dialogo-fondo"
      onMouseDown={e => { if (e.target === e.currentTarget && !nivel3) cerrar(false); }}
    >
      <div
        ref={cajaRef}
        className="dialogo"
        role={nivel3 ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-labelledby="dialogo-titulo"
        onKeyDown={alTeclear}
      >
        <div className="dialogo-cabecera">
          <h3 id="dialogo-titulo">{dialogo.titulo}</h3>
          <BotonIcono className="dialogo-cerrar" etiqueta={resultado ? 'Cerrar' : 'Cancelar'} onClick={() => cerrar(resultado ? true : false)}><span aria-hidden="true">×</span></BotonIcono>
        </div>

        {resultado ? (
          <>
            <div className="dialogo-cuerpo" role="status">{resultado}</div>
            <div className="dialogo-botones">
              <button ref={el => el && el.focus()} type="button" className="dialogo-btn principal" onClick={() => cerrar(true)}>
                Cerrar
              </button>
            </div>
          </>
        ) : (
        <>
        <div className="dialogo-cuerpo">
          {dialogo.alternativa && (
            <div className="dialogo-alternativa">
              <p><strong>{dialogo.alternativa.titulo}</strong> {dialogo.alternativa.descripcion}</p>
              <button type="button" className="dialogo-btn secundario" onClick={() => cerrar('alternativa')} disabled={enCurso}>
                {dialogo.alternativa.texto}
              </button>
            </div>
          )}

          {typeof dialogo.cuerpo === 'string' ? <p>{dialogo.cuerpo}</p> : dialogo.cuerpo}

          {dialogo.eleccion && (
            <fieldset className="dialogo-eleccion">
              <legend>{dialogo.eleccion.etiqueta}</legend>
              {dialogo.eleccion.opciones.map(o => (
                <label key={o.valor} className={elegido === o.valor ? 'elegida' : ''}>
                  <input
                    type="radio"
                    name="dialogo-eleccion"
                    value={o.valor}
                    checked={elegido === o.valor}
                    onChange={() => setElegido(o.valor)}
                    disabled={enCurso}
                  />
                  <span>
                    <strong>{o.texto}</strong>
                    {o.detalle && <small>{o.detalle}</small>}
                  </span>
                </label>
              ))}
            </fieldset>
          )}

          {dialogo.motivo && (
            <label className="dialogo-motivo">
              <span>{dialogo.motivo.etiqueta}</span>
              <textarea
                rows={3}
                value={motivo}
                onChange={e => setMotivo(e.target.value)}
                placeholder={dialogo.motivo.ejemplo || ''}
                maxLength={500}
                disabled={enCurso}
              />
            </label>
          )}

          {dialogo.confirmarTexto && (
            <label className="dialogo-confirmar-texto">
              <span>Para confirmar, escribe <strong>{dialogo.confirmarTexto}</strong>:</span>
              <input
                ref={inputRef}
                type="text"
                value={escrito}
                onChange={e => setEscrito(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); confirmarAccion(); } }}
                autoComplete="off"
                spellCheck={false}
                disabled={enCurso}
              />
            </label>
          )}

          {enCurso && progreso && <p className="dialogo-progreso" role="status">{progreso}</p>}
          {error && <p className="dialogo-error" role="alert">{error}</p>}
        </div>

        <div className="dialogo-botones">
          {enCurso && dialogo.segundoPlano ? (
            <button type="button" className="dialogo-btn secundario" onClick={() => cerrar('segundo-plano', { aunqueEnCurso: true })}>
              {dialogo.segundoPlano}
            </button>
          ) : (
            <button ref={cancelarRef} type="button" className="dialogo-btn secundario" onClick={() => cerrar(false)} disabled={enCurso}>
              {dialogo.cancelar || 'Cancelar'}
            </button>
          )}
          <button
            ref={accionRef}
            type="button"
            className={`dialogo-btn ${dialogo.peligro ? 'peligro' : 'principal'}`}
            onClick={confirmarAccion}
            disabled={!textoCoincide || enCurso}
            aria-busy={enCurso}
          >
            {enCurso && <span className="dialogo-spinner" aria-hidden="true" />}
            {enCurso ? (dialogo.accionEnCurso || 'Procesando…') : dialogo.accion}
          </button>
        </div>
        </>
        )}
      </div>
    </div>
  );
}

// ───────────── Avisos / toasts (base de C-011) ─────────────

let agregarAviso = null;
let siguienteAviso = 1;

/**
 * Muestra un aviso.
 *   tipo      'exito' | 'error' | 'info'
 *   texto     Mensaje en lenguaje humano.
 *   deshacer  función opcional → botón "Deshacer" (el aviso dura 8 s).
 *   accion    { texto, alHacer } opcional → botón con otro texto (ej. "Pedir acceso…", C-028).
 *   fijo      true → no se cierra solo (ej. "listo para revisión", C-037).
 * Los errores no se cierran solos.
 */
export const avisar = ({ tipo = 'exito', texto, deshacer, accion, fijo = false } = {}) => {
  if (!agregarAviso) return;
  agregarAviso({ id: siguienteAviso++, tipo, texto, deshacer, accion, fijo });
};

/**
 * Aviso de error del servidor (C-011): mensaje humano con qué hacer; el detalle técnico va a la consola.
 *   accion  lo que se intentó, en infinitivo: 'guardar el cliente' → "No se pudo guardar el cliente."
 *   err     el error. Si el servidor respondió 4xx con un motivo ("El usuario ya existe"), se muestra;
 *           si fue conexión o error interno, se sugiere revisar la conexión e intentar de nuevo.
 */
export const avisarError = (accion, err) => {
  console.error(`No se pudo ${accion}:`, err);
  const motivo = typeof err === 'string' ? err : err?.message;
  const esMotivoDeNegocio = err?.status >= 400 && err?.status < 500 && motivo && motivo !== 'Error en la petición';
  const detalle = esMotivoDeNegocio
    ? ` ${motivo.trim().replace(/[.\s]*$/, '')}.`
    : ' Revisa tu conexión e intenta de nuevo.';
  avisar({ tipo: 'error', texto: `No se pudo ${accion}.${detalle}` });
};

export function AvisosHost() {
  const [avisos, setAvisos] = useState([]);

  useEffect(() => {
    agregarAviso = (aviso) => {
      setAvisos(prev => [...prev, aviso]);
      if (aviso.tipo !== 'error' && !aviso.fijo) {
        setTimeout(() => setAvisos(prev => prev.filter(a => a.id !== aviso.id)), aviso.deshacer || aviso.accion ? 8000 : 5000);
      }
    };
    return () => { agregarAviso = null; };
  }, []);

  const quitar = (id) => setAvisos(prev => prev.filter(a => a.id !== id));

  return (
    <div className="avisos" aria-live="polite">
      {avisos.map(a => (
        <div key={a.id} className={`aviso ${a.tipo}`} role={a.tipo === 'error' ? 'alert' : 'status'}>
          <span>{a.texto}</span>
          {a.deshacer && (
            <button type="button" className="aviso-deshacer" onClick={() => { quitar(a.id); a.deshacer(); }}>
              Deshacer
            </button>
          )}
          {a.accion && (
            <button type="button" className="aviso-deshacer" onClick={() => { quitar(a.id); a.accion.alHacer(); }}>
              {a.accion.texto}
            </button>
          )}
          <BotonIcono className="aviso-cerrar" etiqueta="Cerrar aviso" onClick={() => quitar(a.id)}><span aria-hidden="true">×</span></BotonIcono>
        </div>
      ))}
    </div>
  );
}
